
import React from 'react';

const StationNetworkAnalysis = ({ competitor }) => {
  // This component would include geospatial analysis of the competitor's station network
  // For now, we'll just render a simple placeholder
  
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-xl font-medium text-gray-900 mb-4">Station Network Distribution</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="text-lg font-semibold text-purple-700 mb-1">{competitor?.stations || '--'}</div>
            <div className="text-sm text-gray-600">Total Stations</div>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="text-lg font-semibold text-blue-700 mb-1">42%</div>
            <div className="text-sm text-gray-600">Urban Locations</div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="text-lg font-semibold text-green-700 mb-1">35%</div>
            <div className="text-sm text-gray-600">Highway Locations</div>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="text-lg font-semibold text-yellow-700 mb-1">23%</div>
            <div className="text-sm text-gray-600">Rural Locations</div>
          </div>
        </div>
        
        <div className="p-4 bg-gray-100 rounded-lg flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-gray-500 mb-2">Network Map Visualization</div>
            <div className="text-sm text-gray-400">
              A map showing the distribution of {competitor?.brand || 'competitor'} stations would appear here
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-xl font-medium text-gray-900 mb-4">Station Quality Analysis</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-4 border border-gray-200 rounded-lg">
            <h4 className="text-lg font-medium text-gray-800 mb-3">Facility Quality</h4>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Store Size</span>
                  <span className="text-sm font-medium">7.5/10</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-purple-600 h-2 rounded-full" style={{ width: '75%' }}></div>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Restroom Cleanliness</span>
                  <span className="text-sm font-medium">8.2/10</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-purple-600 h-2 rounded-full" style={{ width: '82%' }}></div>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Forecourt Design</span>
                  <span className="text-sm font-medium">8.7/10</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-purple-600 h-2 rounded-full" style={{ width: '87%' }}></div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-4 border border-gray-200 rounded-lg">
            <h4 className="text-lg font-medium text-gray-800 mb-3">Amenities</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center p-2 border border-gray-200 rounded">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <span className="text-sm">Food Service: 94%</span>
              </div>
              
              <div className="flex items-center p-2 border border-gray-200 rounded">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <span className="text-sm">Car Wash: 78%</span>
              </div>
              
              <div className="flex items-center p-2 border border-gray-200 rounded">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <span className="text-sm">ATM: 86%</span>
              </div>
              
              <div className="flex items-center p-2 border border-gray-200 rounded">
                <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                <span className="text-sm">EV Charging: 32%</span>
              </div>
              
              <div className="flex items-center p-2 border border-gray-200 rounded">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <span className="text-sm">WiFi: 65%</span>
              </div>
              
              <div className="flex items-center p-2 border border-gray-200 rounded">
                <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                <span className="text-sm">Coffee: 89%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-xl font-medium text-gray-900 mb-4">Network Growth Strategy</h3>
        
        <div className="p-4 border border-gray-200 rounded-lg">
          <h4 className="text-lg font-medium text-purple-700 mb-3">Expansion Pattern Analysis</h4>
          <p className="text-gray-600 mb-4">
            {competitor?.brand || 'Competitor'} has been expanding primarily in suburban areas with 65% of new builds in 
            the last 24 months focused on high-traffic suburban corridors. Their selection criteria 
            emphasizes locations with strong neighborhood growth metrics.
          </p>
          
          <div className="bg-yellow-50 p-3 border border-yellow-200 rounded-md">
            <h5 className="font-medium text-gray-900 mb-1">Competitive Alert</h5>
            <p className="text-sm text-gray-600">
              Planned expansion into 15 new locations in the Northern region rural segment over the next 12 months, 
              with 4 sites already operational.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StationNetworkAnalysis;
